import { supabase } from '../context/AuthContext';
import logger from '../utils/logger';

// Get API URL from environment or use current origin
const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

// Helper function to decode Unicode escape sequences
const decodeUnicode = (str) => {
  if (!str || typeof str !== 'string') return str;
  
  return str.replace(/\\u[\dA-F]{4}/gi, (match) => {
    return String.fromCharCode(parseInt(match.replace(/\\u/g, ''), 16));
  });
};

const ConversationsService = {
  /**
   * Update a message in a conversation after streaming completes
   */
  updateMessage: async ({ sessionId, conversationId, prompt, response, citations = [], messageId, userId }) => {
    try {
      logger.debug('ConversationsService: Updating message with response length:', response?.length || 0);
      logger.debug('ConversationsService: Citations being saved:', JSON.stringify(citations));
      
      // Ensure response is properly decoded
      const decodedResponse = typeof response === 'string' ? decodeUnicode(response) : response;
      
      // Get the auth token for API requests
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      if (!token) {
        throw new Error('Authentication token required');
      }
      
      // Try updating via API first
      if (token && sessionId) {
        try {
          // Log the body we're sending to the API
          const requestBody = {
            sessionId,
            conversationId,
            prompt,
            response: decodedResponse,
            citations: citations || [],
            messageId,
            userId
          };
          logger.debug('Sending message update with citations:', JSON.stringify(requestBody.citations));
          
          const updateResponse = await fetch(`${API_URL}/api/agent/conversations/messages/update`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(requestBody)
          });
          
          if (updateResponse.ok) {
            logger.debug('Successfully updated message via API');
            return { success: true, source: 'api' };
          }
          
          logger.warn('API update failed, trying direct Supabase update');
        } catch (apiError) {
          logger.error('API call failed, falling back to direct DB update:', apiError);
        }
      }
      
      // API call failed, try direct Supabase update
      // First, check for placeholder messages with '[Streaming response in progress...]'
      const { data: placeholders } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .eq('user_query', prompt)
        .eq('openai_response', '[Streaming response in progress...]');
      
      if (placeholders && placeholders.length > 0) {
        logger.debug('Found placeholder messages to update:', placeholders.length);
        // Update existing placeholder
        // eslint-disable-next-line no-unused-vars
        const { data, error: updateError } = await supabase
          .from('messages')
          .update({ 
            openai_response: decodedResponse,
            citations,
            metadata: {
              ...(placeholders[0].metadata || {}),
              messageId,
              streaming: false,
              streamCompleted: true
            }
          })
          .eq('id', placeholders[0].id)
          .select()
          .single();
        
        if (updateError) {
          logger.error('Error updating placeholder message:', updateError);
          throw updateError;
        }
        
        logger.debug('Successfully updated placeholder message');
        return { success: true, source: 'direct_db_placeholder' };
      } 
      
      // Look for any message with this user query to update
      logger.debug('No placeholder found, looking for any matching message...');
      const { data: existingMessages } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .eq('user_query', prompt);
      
      if (existingMessages && existingMessages.length > 0) {
        // Find a message to update (prefer one with empty response)
        const messageToUpdate = existingMessages.find(m => !m.openai_response || m.openai_response === '') 
          || existingMessages[0];
        
        logger.debug('Found existing message to update');
        // eslint-disable-next-line no-unused-vars
        const { data, error: updateError } = await supabase
          .from('messages')
          .update({ 
            openai_response: decodedResponse,
            citations,
            metadata: {
              ...(messageToUpdate.metadata || {}),
              messageId,
              streaming: false,
              streamCompleted: true
            }
          })
          .eq('id', messageToUpdate.id)
          .select()
          .single();
        
        if (updateError) {
          logger.error('Error updating existing message:', updateError);
          throw updateError;
        }
        
        return { success: true, source: 'direct_db_existing' };
      }
      
      // No existing message found, create new one
      logger.debug('No existing messages found, creating new record');
      // eslint-disable-next-line no-unused-vars
      const { data, error: insertError } = await supabase
        .from('messages')
        .insert({
          conversation_id: conversationId,
          user_id: userId,
          user_query: prompt,
          openai_response: decodedResponse,
          citations,
          metadata: {
            messageId,
            streaming: false,
            streamCompleted: true
          }
        })
        .select()
        .single();
      
      if (insertError) {
        logger.error('Error inserting new message:', insertError);
        throw insertError;
      }
      
      return { success: true, source: 'direct_db_new' };
    } catch (error) {
      logger.error('Error in ConversationsService.updateMessage:', error);
      throw error;
    }
  },
  
  /**
   * Get all messages for a conversation
   */
  getMessages: async (sessionId, page = 1, order = 'asc') => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      if (!token) {
        throw new Error('Authentication token required');
      }
      
      const response = await fetch(`${API_URL}/api/agent/conversations/${sessionId}/messages?page=${page}&order=${order}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Error getting messages: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      logger.error('Error in ConversationsService.getMessages:', error);
      throw error;
    }
  },
};

export default ConversationsService; 